import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Flex, Box } from "reflexbox";
import CI from "@trackcode/ci";

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  border: 1px solid ${CI.color.lightGray};
`;

const CardTitle = styled.h3`
  margin: 0 0 16px 0;
  color: ${CI.color.darkGray};
  font-size: 16px;
  font-weight: 600;
`;

const MetricItem = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid ${CI.color.lightGray};
  
  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: ${CI.color.mediumGray};
  margin-bottom: 4px;
`;

const MetricValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${CI.color.darkGray};
`;

const OneTimeAppRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const OneTimeAppKpiRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: baseline;
  column-gap: 12px;
`;

const OneTimeAppKpiLabel = styled.span`
  font-size: 13px;
  color: ${CI.color.mediumGray};
`;

const OneTimeAppKpiCurrentValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${CI.color.darkGray};
`;

const OneTimeAppDelta = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => {
    if (props.trend === "up") return "#2f8f46";
    if (props.trend === "down") return "#c0392b";
    return CI.color.mediumGray;
  }};
  white-space: nowrap;
`;

const OneTimeAppDeltaPercent = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${CI.color.mediumGray};
  margin-left: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${CI.color.mediumGray};
  font-style: italic;
`;

const DeltaLine = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
  color: ${props => {
    if (props.trend === "up") return "#2f8f46";
    if (props.trend === "down") return "#c0392b";
    return CI.color.mediumGray;
  }};
`;

const DeltaContext = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${CI.color.mediumGray};
  margin-left: 6px;
`;

const MemberDebugList = styled.ul`
  margin: 8px 0 0;
  padding: 8px 10px;
  list-style: none;
  background: #f4f6f8;
  border-radius: 4px;
  max-height: 160px;
  overflow-y: auto;
`;

const MemberDebugItem = styled.li`
  font-size: 11px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: ${CI.color.darkGray};
  line-height: 1.45;
  white-space: nowrap;
`;

const MemberDebugMeta = styled.span`
  color: ${CI.color.mediumGray};
`;

const OneTimeAppPreviousValue = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${CI.color.mediumGray};
  margin-left: 6px;
`;

const buildComparisonMap = (comparisonData = []) =>
  comparisonData.reduce((acc, item) => {
    const key = item.companyId ?? item.companyName;
    if (key !== undefined && key !== null) {
      acc[key] = item;
    }
    return acc;
  }, {});

const hasOneTimeAppUsage = (item = {}) =>
  (item.apps ?? 0) > 0 || (item.sms ?? 0) > 0 || (item.liveTracking ?? 0) > 0;

const getDeltaMeta = (currentValue = 0, previousValue = 0) => {
  const diff = currentValue - previousValue;
  const trend = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  const arrow = trend === "up" ? "▲" : trend === "down" ? "▼" : "";
  const signedDiff = diff > 0 ? `+${diff}` : `${diff}`;
  const percent = previousValue === 0 ? null : Math.round((diff / previousValue) * 100);

  return {
    trend,
    arrow,
    signedDiff,
    percent
  };
};

const renderDeltaText = (currentValue, previousValue, comparisonLabel) => {
  const delta = getDeltaMeta(currentValue, previousValue);
  const percentText =
    delta.percent !== null ? ` (${delta.percent > 0 ? `+${delta.percent}` : delta.percent}%)` : "";

  return (
    <DeltaLine trend={delta.trend}>
      {delta.arrow} {delta.signedDiff}
      {percentText}
      <DeltaContext>
        vs. {comparisonLabel}: {previousValue}
      </DeltaContext>
    </DeltaLine>
  );
};

const formatWarehouseScanCount = (n) =>
  typeof n === "number" ? new Intl.NumberFormat("de-DE").format(n) : "";

const MetricsCard = ({ title, data, comparisonData, comparisonLabel, type }) => {
  const comparisonMap = buildComparisonMap(comparisonData);

  const getComparisonItem = (item) => {
    const key = item.companyId ?? item.companyName;
    return comparisonMap[key];
  };

  const renderUsersData = () => {
    if (!data || data.length === 0) {
      return <EmptyState>Keine Daten verfügbar</EmptyState>;
    }

    return data.map((item, index) => (
      <MetricItem key={index}>
        <MetricLabel>{item.companyName || `Logistikunternehmen ${item.companyId}`}</MetricLabel>
        <MetricValue>{item.userCount} Nutzer</MetricValue>
        {renderDeltaText(item.userCount ?? 0, getComparisonItem(item)?.userCount ?? 0, comparisonLabel)}
      </MetricItem>
    ));
  };

  const renderStatusData = () => {
    if (!data || data.length === 0) {
      return <EmptyState>Keine Daten verfügbar</EmptyState>;
    }

    return data.map((item, index) => (
      <MetricItem key={index}>
        <MetricLabel>{item.companyName || `Logistikunternehmen ${item.companyId}`}</MetricLabel>
        <MetricValue>{item.statusCount} Status-Updates</MetricValue>
        {renderDeltaText(item.statusCount ?? 0, getComparisonItem(item)?.statusCount ?? 0, comparisonLabel)}
      </MetricItem>
    ));
  };

  const renderRevenueData = () => {
    if (!data || data.length === 0) {
      return <EmptyState>Keine Daten verfügbar</EmptyState>;
    }

    return data.map((item, index) => (
      <MetricItem key={index}>
        <MetricLabel>{item.companyName || `Logistikunternehmen ${item.companyId}`}</MetricLabel>
        <MetricValue>€{item.statusCount.toFixed(2)}</MetricValue>
      </MetricItem>
    ));
  };

  const renderWarehouseScansData = () => {
    if (!data || data.length === 0) {
      return <EmptyState>Keine Daten verfügbar</EmptyState>;
    }

    const item = data[0];

    return (
      <MetricItem>
        <MetricLabel>{item.companyName || `Logistikunternehmen ${item.companyId}`}</MetricLabel>
        <MetricValue>
          {formatWarehouseScanCount(item.warehouseScans)} Lager-Scans
        </MetricValue>
        {renderDeltaText(
          item.warehouseScans ?? 0,
          getComparisonItem(item)?.warehouseScans ?? 0,
          comparisonLabel
        )}
      </MetricItem>
    );
  };

  const renderOneTimeAppData = () => {
    if (!data || data.length === 0) {
      return <EmptyState>Keine Daten verfügbar</EmptyState>;
    }

    const itemsWithUsage = data.filter(item => {
      const comparisonItem = getComparisonItem(item);
      return hasOneTimeAppUsage(item) || hasOneTimeAppUsage(comparisonItem);
    });

    if (itemsWithUsage.length === 0) {
      return <EmptyState>Keine One-Time-App Nutzung im Zeitraum</EmptyState>;
    }

    return itemsWithUsage.map((item, index) => {
      const comparisonItem = getComparisonItem(item) || {};
      const metrics = [
        { label: "Apps", current: item.apps ?? 0, previous: comparisonItem.apps ?? 0 },
        { label: "SMS", current: item.sms ?? 0, previous: comparisonItem.sms ?? 0 },
        {
          label: "Live Tracking",
          current: item.liveTracking ?? 0,
          previous: comparisonItem.liveTracking ?? 0
        }
      ];

      return (
        <MetricItem key={index}>
          <MetricLabel>{item.companyName || `Logistikunternehmen ${item.companyId}`}</MetricLabel>
          <OneTimeAppRow>
            {metrics.map(metric => {
              const delta = getDeltaMeta(metric.current, metric.previous);

              return (
                <OneTimeAppKpiRow key={metric.label}>
                  <OneTimeAppKpiLabel>{metric.label}</OneTimeAppKpiLabel>
                  <OneTimeAppKpiCurrentValue>{metric.current}</OneTimeAppKpiCurrentValue>
                  <OneTimeAppDelta trend={delta.trend}>
                    {delta.arrow} {delta.signedDiff}
                    {delta.percent !== null && (
                      <OneTimeAppDeltaPercent>
                        ({delta.percent > 0 ? `+${delta.percent}` : delta.percent}%)
                      </OneTimeAppDeltaPercent>
                    )}
                    <OneTimeAppPreviousValue>
                      {comparisonLabel}: {metric.previous}
                    </OneTimeAppPreviousValue>
                  </OneTimeAppDelta>
                </OneTimeAppKpiRow>
              );
            })}
          </OneTimeAppRow>
        </MetricItem>
      );
    });
  };

  return (
    <Card>
      <CardTitle>{title}</CardTitle>
      {type === "users" && renderUsersData()}
      {type === "revenue" && renderRevenueData()}
      {type === "status" && renderStatusData()}
      {type === "warehouseScans" && renderWarehouseScansData()}
      {type === "oneTimeApp" && renderOneTimeAppData()}
    </Card>
  );
};

MetricsCard.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array,
  comparisonData: PropTypes.array,
  comparisonLabel: PropTypes.string,
  type: PropTypes.oneOf(["users", "status", "revenue", "oneTimeApp", "warehouseScans"]).isRequired
};

export default MetricsCard;
